import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Forms, Modal, SaveButton, TitleBar, SystemText } from 'e4shared';
import { apiPatch, apiPost } from 'services';
import { toggleFormHelp, setShowSuccess } from 'actions/ui';
import Files from 'components/admin/forms/files';
import { uploadType } from 'helpers/azure';

export const ManageQuestion = ({ question, show, onClose, onReload }) => {
    const dispatch = useDispatch();

    const [saving, setSaving] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [warnings, setWarnings] = React.useState({ shown: {} });
    const [values, setValues] = React.useState({});
    const { guid, administrator } = useSelector(state => state.user.user);
    const { tags, handles } = useSelector(state => state.ui);
    const competencies = useSelector(state => state.user.downlineCompetencies);
    const competenciesMapped = competencies ? competencies.map(c => ({ id: c.id, name: `${c.area} - ${c.name}` })) : [];
    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        setWarnings({ shown: {} });
    }, [show]);

    React.useEffect(() => {
        setValues({ ...question, own: question.own || question.owner_Id === user.id });
    }, [question]);

    const saveComplete = () => {
        setErrors({});
        setSaving(false);
        dispatch(setShowSuccess(SystemText('KnowledgeBank/ManageQuestion/Message/SaveSuccess')));
        onClose() && onClose();
        onReload?.();
    };

    const saveFailed = e => {
        setErrors(e);
        setSaving(false);
    };

    const closeModal = () => {
        setErrors({});
        setWarnings({ shown: {} });
        setValues({});
        onClose?.();
    };

    const onSave = () => {
        const saving = { ...values };
        let tempErrors = {};
        let tempWarnings = { ...warnings };
        let noErrors = true;
        if (!saving.name) {
            tempErrors = { ...tempErrors, name: SystemText('KnowledgeBank/ManageQuestion/Fields/Question/Error') };
            noErrors = false;
        }

        if (!saving.description) {
            tempErrors = { ...tempErrors, description: SystemText('KnowledgeBank/ManageQuestion/Fields/Details/Error') };
            noErrors = false;
        }

        if (!saving.id && saving.description && saving.description.indexOf('#') < 0 && !tempWarnings.shown.description) {
            tempWarnings.description = SystemText('KnowledgeBank/ManageQuestion/Fields/Tags/Error');
            tempWarnings.shown = { ...tempWarnings.shown, description: true };
            noErrors = false;
        }

        if (saving.own && !saving.id) {
            if (!saving.answer) {
                tempErrors = { ...tempErrors, answer: SystemText('KnowledgeBank/ManageQuestion/Fields/Answer/Error') };
                noErrors = false;
            } else {
                saving.answers = [{ answer: saving.answer, assets: saving.assets }];
            }
        }

        if (!saving.id && !saving.competency_Ids && !tempWarnings.shown.competency_Ids) {
            tempWarnings.competency_Ids = SystemText('KnowledgeBank/ManageQuestion/Fields/Competencies/Error');
            tempWarnings.shown = { ...tempWarnings.shown, competency_Ids: true };
            noErrors = false;
        }

        setWarnings(tempWarnings);

        const responses = {
            ok: response => {
                if (response.ok) {
                    saveComplete();
                } else {
                    tempErrors = {
                        ...tempErrors,
                        question: SystemText('KnowledgeBank/ManageQuestion/Fields/General/Error'),
                    };
                    saveFailed(tempErrors);
                }
            },
            error: error => {
                tempErrors = { ...tempErrors, question: SystemText('KnowledgeBank/ManageQuestion/Fields/General/Error') };
                saveFailed(tempErrors);
            },
        };

        if (noErrors) {
            if (saving.id) {
                apiPatch('question-bank/question', saving, guid, responses.ok, responses.error);
            } else {
                apiPost('question-bank/question', saving, guid, responses.ok, responses.error);
            }
        } else {
            responses.error();
        }
    };

    const setValue = (prop, value) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const footer = (
        <div className="pull-right">
            <SaveButton
                data-testid="button-save"
                saving={saving}
                setSaving={setSaving}
                className="btn btn-success"
                onClick={() => onSave()}
            >
                {SystemText('KnowledgeBank/ManageQuestion/Buttons/Save')}
            </SaveButton>
            <button data-testid="button-close" className="btn" onClick={() => closeModal()}>
                {SystemText('KnowledgeBank/ManageQuestion/Buttons/Close')}
            </button>
        </div>
    );

    if (!show) return null;

    return (
        <Modal
            show={show}
            title={
                values.id
                    ? SystemText('KnowledgeBank/ManageQuestion/Modal/Edit/Title')
                    : values.own
                    ? SystemText('KnowledgeBank/ManageQuestion/Modal/Share/Title')
                    : SystemText('KnowledgeBank/ManageQuestion/Modal/Add/Title')
            }
            footer={footer}
            close={closeModal}
            toggleFormHelp={() => dispatch(toggleFormHelp())}
        >
            <div id="knowledgeBankQuestion">
                <Forms.Text
                    label={SystemText('KnowledgeBank/ManageQuestion/Fields/Question/Label')}
                    data-testid="text-question"
                    error={errors.name}
                    value={values.name}
                    onChange={value => setValue('name', value)}
                    onSubmit={() => onSave()}
                    mandatory
                    maxLength="100"
                    help={
                        values.own
                            ? SystemText('KnowledgeBank/ManageQuestion/Fields/OwnQuestion/Help')
                            : SystemText('KnowledgeBank/ManageQuestion/Fields/Question/Help')
                    }
                />
                <Forms.Textarea
                    tags={tags}
                    data-testid="text-description"
                    error={errors.description}
                    handles={handles}
                    value={values.description}
                    warning={warnings.description}
                    mandatory
                    help={SystemText('KnowledgeBank/ManageQuestion/Fields/Details/Help')}
                    onChange={value => setValue('description', value)}
                    label={SystemText('KnowledgeBank/ManageQuestion/Fields/Details/Label')}
                    rows={!values.id && values.own ? 6 : 10}
                />
                <Forms.Select
                    value={values.competency_Ids}
                    data-testid="select-competencies"
                    onChange={value => setValue('competency_Ids', value)}
                    label={SystemText('KnowledgeBank/ManageQuestion/Fields/Competencies/Label')}
                    allowMultiple
                    warning={warnings.competency_Ids}
                    data={competenciesMapped}
                    help={SystemText('KnowledgeBank/ManageQuestion/Fields/Competencies/Help')}
                />
                {!values.id && values.own && (
                    <div>
                        <Forms.Textarea
                            data-testid="text-answer"
                            tags={tags}
                            handles={handles}
                            error={errors.answer}
                            value={values.answer}
                            mandatory
                            help={SystemText('KnowledgeBank/ManageQuestion/Fields/Answer/Help')}
                            onChange={value => setValue('answer', value)}
                            label={SystemText('KnowledgeBank/ManageQuestion/Fields/Answer/Label')}
                            rows="6"
                        />
                    </div>
                )}
                {(administrator || values.own) && (
                    <TitleBar
                        title={SystemText('KnowledgeBank/ManageQuestion/MoreOptions/Title')}
                        openTitle={SystemText('KnowledgeBank/ManageQuestion/LessOptions/Title')}
                        className="showMore"
                        data-testid="span-show-more"
                    >
                        {values.own && (
                            <Files
                                data-testid="files-attachments"
                                value={values.assets}
                                onChange={value => setValue('assets', value)}
                                label={SystemText('KnowledgeBank/ManageQuestion/Fields/Attachments/Label')}
                                help={SystemText('KnowledgeBank/ManageQuestion/Fields/Attachments/Help')}
                                folder={uploadType.QuestionAnswer}
                                allowMultiple
                                modalInModal
                            />
                        )}
                        {administrator && (
                            <Forms.Toggle
                                data-testid="toggle-featured"
                                label={SystemText('KnowledgeBank/ManageQuestion/Fields/FeaturedQuestion/Label')}
                                help={SystemText('KnowledgeBank/ManageQuestion/Fields/FeaturedQuestion/Help')}
                                value={values.sticky}
                                onChange={value => setValue('sticky', value)}
                            />
                        )}
                    </TitleBar>
                )}
            </div>
        </Modal>
    );
};

export default ManageQuestion;
